// src/pages/reports/FinancialReport.js
import React, { useState, useEffect } from 'react';
import { 
  getFinancialReport, 
  exportFinancialReport,
  getCompanySettings
} from '../../api/tenant/report.api';
import { toast } from 'react-toastify';
import { FaBuilding, FaCalendarAlt, FaFilePdf } from 'react-icons/fa';

// Suppress native browser alerts for export success messages
const originalAlert = window.alert;
window.alert = function(message) {
  if (message && (message.includes("exported") || message.includes("Financial Report"))) {
    console.log("Suppressed alert:", message);
    return;
  }
  originalAlert(message);
};

const FinancialReport = () => {
  const [financialData, setFinancialData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Fetch company settings
  const fetchCompanySettings = async () => {
    try {
      const response = await getCompanySettings();
      if (response.data?.success) {
        setCompanySettings(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching company settings:", error);
    }
  };

  // Fetch Financial report data
  const fetchFinancialReport = async () => {
    setLoading(true);
    try {
      const response = await getFinancialReport(fromDate, toDate);
      console.log("Financial Report response:", response.data);
      
      if (response.data?.success) {
        setFinancialData(response.data.data);
        // No success toast - silent success
      } else {
        toast.error(response.data?.message || 'Failed to fetch financial report');
      }
    } catch (error) {
      console.error('Error fetching financial report:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch financial report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanySettings();
  }, []);

  useEffect(() => {
    fetchFinancialReport();
  }, [fromDate, toDate]);

  // Handle PDF Export - NO ALERT
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await exportFinancialReport('pdf', fromDate, toDate);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `financial_report_${fromDate}_to_${toDate}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      // No alert - silent success
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(error.response?.data?.message || 'Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  // Get data
  const sales = financialData?.sales || {
    total_invoices: 0,
    total_sales: 0,
    total_gst: 0,
    avg_invoice_value: 0
  };

  const profitLoss = financialData?.profit_loss || {
    total_revenue: 0,
    total_expenses: 0,
    net_profit: 0,
    profit_margin: 0
  };

  const outstanding = financialData?.outstanding || {
    total_outstanding_invoices: 0,
    total_outstanding_amount: 0
  };

  const expenses = financialData?.expenses || [];
  const financialDataChart = financialData?.financial_data || [];
  const salesTrend = financialData?.sales_trend || [];

  return (
    <div className="container-fluid px-4 py-3">
      {/* Company Header with Logo */}
      {companySettings && (
        <div className="card mb-4 shadow-sm border-0">
          <div className="card-body py-3">
            <div className="row align-items-center">
              <div className="col-auto">
                {companySettings.logo ? (
                  <img
                    src={companySettings.logo}
                    alt={companySettings.company_name}
                    style={{ height: "60px", width: "auto" }}
                  />
                ) : (
                  <FaBuilding size={50} color="#4361ee" />
                )}
              </div>
              <div className="col">
                <h3 className="mb-0 fw-bold" style={{ color: companySettings.primary_color || "#4361ee" }}>
                  {companySettings.company_name || "Business Management Pro"}
                </h3>
                {companySettings.brand_tagline && (
                  <p className="text-muted mb-0">{companySettings.brand_tagline}</p>
                )}
                <small className="text-muted">
                  {companySettings.company_email} | {companySettings.company_phone}
                </small>
              </div>
              <div className="col-auto text-end">
                <div className="text-muted small">
                  <strong>GST:</strong> {companySettings.gst_number || "N/A"}<br />
                  <strong>PAN:</strong> {companySettings.pan_number || "N/A"}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header and Filters */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2 className="mb-0">Financial Report</h2>
          <p className="text-muted mb-0">
            <FaCalendarAlt className="me-1" size={12} />
            {fromDate} to {toDate}
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <input
            type="date"
            className="form-control"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            style={{ width: '150px' }}
          />
          <input
            type="date"
            className="form-control"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            style={{ width: '150px' }}
          />
          <button 
            onClick={fetchFinancialReport} 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="mb-4">
        <button 
          onClick={handleExportPDF} 
          className="btn btn-danger"
          disabled={exporting || !financialData}
        >
          <FaFilePdf className="me-1" />
          {exporting ? 'Exporting...' : 'Export PDF'}
        </button>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Fetching financial data...</p>
        </div>
      ) : financialData ? (
        <>
          {/* Profit & Loss Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-4 mb-3">
              <div className="card bg-success text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Total Revenue</h6>
                  <h3 className="mb-0">{formatCurrency(profitLoss.total_revenue)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="card bg-danger text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Total Expenses</h6>
                  <h3 className="mb-0">{formatCurrency(profitLoss.total_expenses)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-4 mb-3">
              <div className="card bg-primary text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Net Profit</h6>
                  <h3 className="mb-0">{formatCurrency(profitLoss.net_profit)}</h3>
                  <small className="text-white-50">Margin: {profitLoss.profit_margin}%</small>
                </div>
              </div>
            </div>
          </div>

          {/* Sales Summary Cards */}
          <div className="row mb-4">
            <div className="col-md-3 mb-3">
              <div className="card bg-info text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Total Invoices</h6>
                  <h3 className="mb-0">{sales.total_invoices}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-primary text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Total Sales</h6>
                  <h3 className="mb-0">{formatCurrency(sales.total_sales)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-warning text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Total GST</h6>
                  <h3 className="mb-0">{formatCurrency(sales.total_gst)}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-3 mb-3">
              <div className="card bg-secondary text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Avg Invoice Value</h6>
                  <h3 className="mb-0">{formatCurrency(sales.avg_invoice_value)}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Outstanding Summary */}
          <div className="row mb-4">
            <div className="col-md-6 mb-3">
              <div className="card bg-dark text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Outstanding Invoices</h6>
                  <h3 className="mb-0">{outstanding.total_outstanding_invoices}</h3>
                </div>
              </div>
            </div>
            <div className="col-md-6 mb-3">
              <div className="card bg-danger text-white h-100 shadow-sm">
                <div className="card-body">
                  <h6 className="card-title">Outstanding Amount</h6>
                  <h3 className="mb-0">{formatCurrency(outstanding.total_outstanding_amount)}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Financial Trend */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">Monthly Profit vs Expenses</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Month</th>
                      <th>Profit (Sales)</th>
                      <th>Expenses</th>
                      <th>Net Profit/Loss</th>
                      <th>Profit Margin</th>
                    </tr>
                  </thead>
                  <tbody>
                    {financialDataChart && financialDataChart.length > 0 ? (
                      financialDataChart.map((item, index) => {
                        const netProfit = (item.profit || 0) - (item.expenses || 0);
                        const profitMargin = item.profit > 0 ? ((netProfit / item.profit) * 100).toFixed(2) : 0;
                        return (
                          <tr key={index}>
                            <td>{item.month} {item.month_num}</td>
                            <td className="text-success">{formatCurrency(item.profit || 0)}</td>
                            <td className="text-danger">{formatCurrency(item.expenses || 0)}</td>
                            <td className={netProfit >= 0 ? "text-success fw-bold" : "text-danger fw-bold"}>
                              {formatCurrency(netProfit)}
                            </td>
                            <td>
                              <span className={`badge ${netProfit >= 0 ? 'bg-success' : 'bg-danger'}`}>
                                {profitMargin}%
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center text-muted py-4">No monthly financial data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Expense Breakdown Table */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">Expense Breakdown by Category</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Category</th>
                      <th>Number of Expenses</th>
                      <th>Total Amount</th>
                      <th>Percentage of Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses && expenses.length > 0 ? (
                      expenses.map((expense, index) => (
                        <tr key={index}>
                          <td>
                            <span className="badge bg-info">
                              {expense.category || 'Other'}
                            </span>
                          </td>
                          <td>{expense.category_count || 0}</td>
                          <td className="text-danger fw-bold">{formatCurrency(expense.category_amount || 0)}</td>
                          <td>
                            {profitLoss.total_expenses > 0 
                              ? ((expense.category_amount / profitLoss.total_expenses) * 100).toFixed(2) 
                              : 0}%
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">No expense data available</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="table-light">
                    <tr>
                      <th>Total</th>
                      <th>{expenses.reduce((sum, exp) => sum + (exp.category_count || 0), 0)}</th>
                      <th>{formatCurrency(profitLoss.total_expenses)}</th>
                      <th>100%</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>

          {/* Sales Trend */}
          <div className="card mb-4 shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">Sales Trend (Monthly)</h5>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-bordered table-hover mb-0">
                  <thead className="table-dark">
                    <tr>
                      <th>Month</th>
                      <th>Sales Amount</th>
                      <th>Growth</th>
                    </tr>
                  </thead>
                  <tbody>
                    {salesTrend && salesTrend.length > 0 ? (
                      salesTrend.map((item, index) => {
                        const prevSales = index > 0 ? salesTrend[index - 1].sales : item.sales;
                        const growth = ((item.sales - prevSales) / prevSales * 100).toFixed(2);
                        const isGrowth = item.sales >= prevSales;
                        return (
                          <tr key={index}>
                            <td>{item.month} {item.month_num}</td>
                            <td className="fw-bold">{formatCurrency(item.sales)}</td>
                            <td>
                              {index > 0 ? (
                                <span className={`badge ${isGrowth ? 'bg-success' : 'bg-danger'}`}>
                                  {isGrowth ? '+' : ''}{growth}%
                                </span>
                              ) : (
                                <span className="badge bg-secondary">Base</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="3" className="text-center text-muted py-4">No sales trend data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Key Financial Ratios */}
          <div className="card shadow-sm">
            <div className="card-header bg-white">
              <h5 className="mb-0">Key Financial Ratios</h5>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <div className="alert alert-info text-center">
                    <h6>Profit Margin</h6>
                    <h3 className="mb-0">{profitLoss.profit_margin}%</h3>
                    <small>Net Profit / Total Revenue</small>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="alert alert-warning text-center">
                    <h6>Expense Ratio</h6>
                    <h3 className="mb-0">
                      {profitLoss.total_revenue > 0 
                        ? ((profitLoss.total_expenses / profitLoss.total_revenue) * 100).toFixed(2)
                        : 0}%
                    </h3>
                    <small>Total Expenses / Total Revenue</small>
                  </div>
                </div>
                <div className="col-md-4 mb-3">
                  <div className="alert alert-success text-center">
                    <h6>Average Transaction Value</h6>
                    <h3 className="mb-0">{formatCurrency(sales.avg_invoice_value)}</h3>
                    <small>Total Sales / Total Invoices</small>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="alert alert-danger text-center">
                    <h6>Outstanding Ratio</h6>
                    <h3 className="mb-0">
                      {sales.total_sales > 0 
                        ? ((outstanding.total_outstanding_amount / sales.total_sales) * 100).toFixed(2)
                        : 0}%
                    </h3>
                    <small>Outstanding Amount / Total Sales</small>
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="alert alert-secondary text-center">
                    <h6>GST to Sales Ratio</h6>
                    <h3 className="mb-0">
                      {sales.total_sales > 0 
                        ? ((sales.total_gst / sales.total_sales) * 100).toFixed(2)
                        : 0}%
                    </h3>
                    <small>Total GST / Total Sales</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className="alert alert-info">No data available for the selected date range</div>
      )}
    </div>
  );
};

export default FinancialReport;